import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { CountUp, RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

/**
 * Headline figures.
 *
 * These count up on entering view, at the client's request. The original build
 * deliberately did not: on a financial advisory site an animated number reads
 * as a performance claim being dramatised, which is not an impression this
 * brand wants to give.
 *
 * That reasoning has not gone away, so two things guard it. The figures are
 * facts about the practice — years, clients, markets — never returns. And the
 * disclaimer below is non-optional: whatever the numbers do, the page states
 * plainly that they are not a forecast or a performance record.
 */
export default function StatisticsBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["statistics"]>) {
  const onDark = isDarkBackground(settings);

  return (
    <SectionShell settings={settings}>
      <SectionHeading eyebrow={content.eyebrow} heading={content.heading} settings={settings} />

      <RevealList
        as="div"
        className={cn(
          "grid gap-x-8 gap-y-10",
          content.stats.length <= 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4",
        )}
      >
        {content.stats.map((stat) => (
          <RevealItem as="div" key={stat.label}>
            <p className={cn("font-display text-5xl", onDark ? "text-gold-300" : "text-navy-900")}>
              {/*
                The counting figure is hidden from assistive technology and the
                settled value announced once beside it. A screen reader should
                hear "twelve", not two hundred intermediate numbers.
              */}
              <span aria-hidden="true">
                {stat.prefix}
                <CountUp value={stat.value} />
                {stat.suffix}
              </span>
              <span className="sr-only">
                {stat.prefix}
                {stat.value}
                {stat.suffix}
              </span>
            </p>
            <p
              className={cn(
                "mt-2 text-sm font-semibold",
                onDark ? "text-cream-100" : "text-navy-800",
              )}
            >
              {stat.label}
            </p>
            {stat.description && (
              <p className={cn("mt-1 text-sm", onDark ? "text-navy-300" : "text-ink-500")}>
                {stat.description}
              </p>
            )}
          </RevealItem>
        ))}
      </RevealList>

      {/*
        The qualifier is part of the block, not an optional extra. Figures on a
        financial site read as claims whether or not they are meant to.
      */}
      {content.disclaimer && (
        <p
          className={cn("mt-10 text-xs leading-relaxed", onDark ? "text-navy-300" : "text-ink-500")}
        >
          {content.disclaimer}
        </p>
      )}
    </SectionShell>
  );
}
